/**
 * OAuthCallbackReconciler — maps a completed Amplify Cognito Hosted-UI session
 * into the app's existing AuthTokens/User shape.
 *
 * Implements google-sign-in/US-001 (T4, AC-001.3). Pure mapping/fetch service, no
 * UI. Consumed by OAuthCallbackPage (T6), which hands the result to
 * useAuth().hydrateFederatedSession (T3) so federated tokens flow through the
 * exact SAME localStorage/Zustand shape as email/password login (AuthGuard,
 * ParentGuard, RootRedirect, api.ts request interceptor, and the 5-min-pre-expiry
 * refresh timer all work unchanged, TO-LL-004).
 *
 * Refresh-token note (T4 step 4 / T3 dependency): Amplify v6's fetchAuthSession()
 * intentionally does NOT surface a raw Cognito refresh token to application code —
 * Amplify manages refresh internally against its own token store. The app's own
 * POST /auth/refresh endpoint expects a real Cognito refresh token, which is not
 * available for federated users. Therefore federated sessions do NOT use
 * POST /auth/refresh at all: getFreshFederatedTokens() (exported here, consumed by
 * useAuth.ts's refresh mutation) re-derives fresh tokens by asking Amplify itself
 * to refresh (fetchAuthSession({forceRefresh:true})) and re-mapping the result into
 * the same RefreshTokenResponse shape (accessToken, idToken, expiresIn) the
 * password-login refresh path already produces — so the SAME useEffect/isRefreshing
 * mutex (TO-LL-004) covers both paths without any branching in the timer itself.
 * tokens.refreshToken is populated with a non-secret placeholder string for
 * federated sessions (never a real secret, BR-001.1) purely so the existing
 * AuthTokens shape validation (all four fields present) is satisfied; it is never
 * sent to the backend for federated users because the refresh branch never calls
 * authApi.refreshToken() for them (see useAuth.ts channel==='google' branch).
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import { getUserProfile } from '@/services/pebbles';
import type { AuthTokens, User, RefreshTokenResponse } from '@/types/auth';

// google-sign-in/US-001 (T4): non-secret placeholder written into AuthTokens.refreshToken
// for federated sessions — federated refresh never reads this value (see module docstring).
export const FEDERATED_REFRESH_TOKEN_PLACEHOLDER = 'amplify-managed';

export type OAuthReconcileErrorCode = 'NO_SESSION' | 'NO_EMAIL' | 'NETWORK';

/**
 * Typed error surfaced to OAuthCallbackPage (T6) so it can map each code to the
 * exact story-mandated user-facing message (edge cases: cancel / no-email / network).
 */
export class OAuthReconcileError extends Error {
  code: OAuthReconcileErrorCode;
  constructor(code: OAuthReconcileErrorCode, message?: string) {
    super(message ?? code);
    this.name = 'OAuthReconcileError';
    this.code = code;
  }
}

/**
 * Compute expiresIn (seconds) from a JWT's `exp` claim, matching the existing
 * tokenExpiry convention (useAuth.ts setTokens: Date.now() + expiresIn * 1000).
 */
function expiresInFromJwtExp(exp: number | undefined): number {
  if (!exp) return 0;
  return Math.max(0, exp - Math.floor(Date.now() / 1000));
}

/**
 * Read the just-completed Amplify Cognito session and map it into the app's
 * AuthTokens shape. Does NOT fetch the parent's profile/email — call
 * fetchParentProfile() separately AFTER hydrateFederatedSession has written the
 * idToken to localStorage (so the api.ts request interceptor can attach it).
 *
 * @throws {OAuthReconcileError} code 'NO_SESSION' if the user cancelled at Google
 * (or any other reason no completed session exists) — AC-001.5.
 */
export async function reconcileOAuthSession(): Promise<{
  tokens: AuthTokens;
  userBase: Pick<User, 'userId'>;
}> {
  const session = await fetchAuthSession({ forceRefresh: false });
  const idToken = session.tokens?.idToken;
  const accessToken = session.tokens?.accessToken;

  if (!idToken || !accessToken) {
    throw new OAuthReconcileError('NO_SESSION', 'No completed Google session found.');
  }

  const tokens: AuthTokens = {
    accessToken: accessToken.toString(),
    idToken: idToken.toString(),
    refreshToken: FEDERATED_REFRESH_TOKEN_PLACEHOLDER,
    expiresIn: expiresInFromJwtExp(idToken.payload.exp),
  };

  const userId = (idToken.payload.sub as string | undefined) ?? '';

  return { tokens, userBase: { userId } };
}

/**
 * Fetch the parent's profile via GET /users/me (getUserProfile, pebbles.ts) —
 * reused verbatim so the axios request interceptor auto-attaches the Bearer
 * idToken once hydrateFederatedSession has written it to localStorage.
 *
 * @throws {OAuthReconcileError} code 'NO_EMAIL' if the response has no email
 * (edge case: provider returned no email), or 'NETWORK' on any transport/API
 * failure (edge case: network failure during callback / token exchange).
 */
export async function fetchParentProfile(): Promise<User> {
  let profile;
  try {
    profile = await getUserProfile();
  } catch {
    // api.ts's response interceptor already transforms this into an AuthError —
    // any failure fetching the profile at this stage is treated as a retriable
    // network failure per the story's edge-case mapping.
    throw new OAuthReconcileError(
      'NETWORK',
      "Sign-in didn't complete. Please check your connection and try again."
    );
  }

  if (!profile.email) {
    throw new OAuthReconcileError(
      'NO_EMAIL',
      "We couldn't get your email from Google. Please try again or sign in with email and password."
    );
  }

  return {
    userId: profile.userId,
    email: profile.email,
    name: profile.displayName,
    channel: 'google',
    // BUG-20260713-020205 (D2, Option B) / T-8: thread consentPending through
    // from GET /users/me (getUserProfile already returns it — pebbles.ts) so
    // OAuthCallbackPage (T-9) can decide whether a login-intent Google user is
    // not-yet-registered and must be bounced to /register.
    consentPending: profile.consentPending,
  };
}

/**
 * Federated token refresh: asks Amplify to refresh its own session and re-maps
 * the result into the RefreshTokenResponse shape the password-login refresh path
 * already produces. Consumed by useAuth.ts's refreshMutation (T3) when the
 * current user's channel === 'google'. See module docstring for the full
 * rationale (Amplify v6 does not surface a raw Cognito refresh token).
 */
export async function getFreshFederatedTokens(): Promise<RefreshTokenResponse> {
  const session = await fetchAuthSession({ forceRefresh: true });
  const idToken = session.tokens?.idToken;
  const accessToken = session.tokens?.accessToken;

  if (!idToken || !accessToken) {
    throw new OAuthReconcileError('NO_SESSION', 'Federated session refresh failed.');
  }

  return {
    accessToken: accessToken.toString(),
    idToken: idToken.toString(),
    expiresIn: expiresInFromJwtExp(idToken.payload.exp),
  };
}
